import { Box, Divider, styled, useTheme } from '@mui/material';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';
import Placeholder from '@tiptap/extension-placeholder';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import useOutsideClick from 'hooks/useOutsideClick';
import { useEffect, useState } from 'react';
import { MemreButton, MemreButtonGroup } from './';
import { Color } from 'core';

const EditorContainer = styled(Box)(({ theme }) => ({
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  minHeight: '40px',
  fontFamily: theme.typography.fontFamily,
  fontSize: theme.typography.body1.fontSize,
  flex: 1,
  '.ProseMirror': {
    border: 'none',
    '&.is-editor-empty:first-child::before': {
      color: Color.secondaryGray,
      content: 'attr(data-placeholder)',
      float: 'left',
      height: 0,
      pointerEvents: 'none',
    },
    'p.is-empty::before': {
      color: Color.secondaryGray,
      content: 'attr(data-placeholder)',
      float: 'left',
      height: 0,
      pointerEvents: 'none',
    },
  },
}));

export default function MemreRTE({
  initialContent = '',
  onUpdate,
  placeholder = 'Type here...',
  id,
}: {
  initialContent?: string;
  placeholder?: string;
  onUpdate: (content: string) => void;
  id: string;
}) {
  const theme = useTheme();
  const [updatedText, setUpdatedText] = useState(initialContent);
  const editor = useEditor({
    extensions: [
      StarterKit,
      Bold,
      Italic,
      Superscript,
      Subscript,
      Placeholder.configure({
        placeholder,
      }),
    ],
    content: initialContent,
    onUpdate: ({ editor: e }) => {
      setUpdatedText(e.getHTML());
      onUpdate(e.getHTML());
    },
  });

  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [showButtons, setShowButtons] = useState(false);

  useEffect(() => {
    if (
      editor &&
      editor.view &&
      !editor.state.selection.empty &&
      editor.state.selection.from &&
      editor.state.selection.to &&
      ref.current
    ) {
      const { from, to } = editor.state.selection;
      const start = editor.view?.coordsAtPos(from) || 0;
      const end = editor.view?.coordsAtPos(to) || 0;

      const refRect = ref.current.getBoundingClientRect();

      setPosition({
        top: Math.min(start.top, end.top) - refRect.top - 40,
        left: (start.left + end.left) / 2 - refRect.left,
      });
      setShowButtons(true);
    } else {
      setShowButtons(false);
    }
  }, [editor?.state.selection]);

  const ref = useOutsideClick<HTMLDivElement>(() => {
    setShowButtons(false);
    if (updatedText !== initialContent) {
      onUpdate(updatedText);
    }
  });

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {showButtons && (
        <MemreButtonGroup
          sx={{
            position: 'absolute',
            top: position.top,
            left: position.left,
            transform: 'translateX(-50%)',
            zIndex: 10,
            backgroundColor: Color.white,
            padding: 1,
            borderRadius: 1,
            boxShadow: theme.shadows[1],
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <MemreButton
            variant="icon"
            size="small"
            iconName="bold"
            active={editor?.isActive('bold')}
            onClick={() => editor?.chain().focus().toggleBold().run()}
          />
          <Divider sx={{ height: 12, m: 0.5, opacity: 0.5 }} orientation="vertical" />
          <MemreButton
            variant="icon"
            size="small"
            iconName="italic"
            active={editor?.isActive('italic')}
            onClick={() => editor?.chain().focus().toggleItalic().run()}
          />
          <Divider sx={{ height: 12, m: 0.5, opacity: 0.5 }} orientation="vertical" />
          <MemreButton
            variant="icon"
            iconName="superscript"
            size="small"
            active={editor?.isActive('superscript')}
            onClick={() => editor?.chain().focus().toggleSuperscript().run()}
          />
          <Divider sx={{ height: 12, m: 0.5, opacity: 0.5 }} orientation="vertical" />
          <MemreButton
            variant="icon"
            size="small"
            iconName="subscript"
            active={editor?.isActive('subscript')}
            onClick={() => editor?.chain().focus().toggleSubscript().run()}
          />
        </MemreButtonGroup>
      )}
      <EditorContainer theme={theme}>
        <EditorContent key={id} editor={editor} autoFocus={true} />
      </EditorContainer>
    </div>
  );
}
